import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  PlusCircle,
  Users,
  ExternalLink,
  LogOut,
  Shield,
  FolderKanban,
  Briefcase,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { adminAuthService } from "@/api/adminAuthService";
import { toast } from "@/components/ui/sonner";

/**
 * ============================================================
 * ADMIN SIDEBAR NAVIGATION
 * ============================================================
 * Persistent desktop navigation shell and drawer content.
 * Provides direct access to Dashboard, Workshops, and System actions.
 */
export default function AdminSidebar({ onItemClick }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    adminAuthService.logout();
    toast.info("You have been signed out of the Admin Console.");
    navigate("/admin/login", { replace: true });
    if (onItemClick) onItemClick();
  };

  const navSections = [
    {
      title: "Main",
      items: [
        {
          label: "Dashboard",
          to: "/admin",
          icon: LayoutDashboard,
          end: true,
        },
      ],
    },
    {
      title: "Project Management",
      items: [
        {
          label: "All Projects",
          to: "/admin/projects",
          icon: FolderKanban,
          end: true,
        },
        {
          label: "New Project",
          to: "/admin/projects/new",
          icon: PlusCircle,
          end: false,
        },
        {
          label: "Taxonomy & Tech",
          to: "/admin/taxonomy",
          icon: Layers,
          end: true,
        },
      ],
    },
    {
      title: "Career Management",
      items: [
        {
          label: "All Positions",
          to: "/admin/careers",
          icon: Briefcase,
          end: true,
        },
        {
          label: "Post New Role",
          to: "/admin/careers/new",
          icon: PlusCircle,
          end: false,
        },
        {
          label: "Applications",
          to: "/admin/careers/applications",
          icon: Users,
          end: false,
        },
      ],
    },
    {
      title: "Workshops Platform",
      items: [
        {
          label: "Workshops",
          to: "/admin/workshops",
          icon: Layers,
          end: true,
        },
        {
          label: "Create Workshop",
          to: "/admin/workshops/new",
          icon: PlusCircle,
          end: false,
        },
      ],
    },
  ];

  return (
    <aside className="w-full flex flex-col justify-between min-h-full bg-white border-r border-slate-200/80 select-none">
      {/* Brand Header */}
      <div>
        <div className="p-6 border-b border-slate-200/80 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
            <img
              src="/Shiveda_Logo.png"
              alt="Shiveda Logo"
              className="w-5 h-auto"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5 leading-tight">
              Shiveda IT
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200 font-mono font-medium">
                Admin
              </span>
            </div>
            <div className="text-[10px] text-slate-500 font-medium">Operations Console</div>
          </div>
        </div>

        {/* Navigation Sections */}
        <div className="p-4 space-y-6">
          {navSections.map((section) => (
            <div key={section.title}>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">
                {section.title}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.end
                    ? location.pathname === item.to
                    : location.pathname.startsWith(item.to);

                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => onItemClick && onItemClick()}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-150",
                        isActive
                          ? "bg-cyan-50 text-cyan-700 border border-cyan-200/80 font-semibold shadow-xs"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Actions & Logout */}
      <div className="p-4 border-t border-slate-200/80 space-y-1">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">
          System
        </div>


        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors text-left"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
